/**
 * AgendaManager — gestion de l'agenda municipal (mairie / GRC).
 *
 * La mairie crée ses événements (conseil, cérémonie, marché, fête…) qui
 * s'affichent côté administré dans « Vie de la commune », à côté des
 * événements associatifs. Greffé dans la page « Infos pratiques ».
 *
 * Conforme au guide : zéro window.confirm (useConfirmDialog + {dialogElement}).
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge, Alert, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { AgendaEvenement, AgendaCategorie } from '@/lib/api';
import { TIERS_CATEGORIES_USAGE } from '@roronoa-collectivites/shared-types';
import { CalendarDays, Plus, Pencil, Trash2, MapPin, Clock, EyeOff, AlertCircle, FileSpreadsheet, FileDown, FileSignature, Send } from 'lucide-react';

const CATEGORIE_LABEL: Record<AgendaCategorie, string> = {
  conseil: 'Conseil municipal', ceremonie: 'Cérémonie', marche: 'Marché', fete: 'Fête',
  culture: 'Culture', sport: 'Sport', reunion: 'Réunion', autre: 'Autre',
};
const CATEGORIE_VARIANT: Record<AgendaCategorie, 'info' | 'success' | 'warning' | 'neutral' | 'grc'> = {
  conseil: 'info', ceremonie: 'grc', marche: 'success', fete: 'warning',
  culture: 'info', sport: 'success', reunion: 'neutral', autre: 'neutral',
};

function frDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function AgendaManager() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [evenements, setEvenements] = useState<AgendaEvenement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<AgendaEvenement | 'new' | null>(null);
  const [diffuse, setDiffuse] = useState<AgendaEvenement | null>(null);
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);

  async function doExport(kind: 'excel' | 'pdf') {
    setExporting(kind); setError(null);
    try {
      if (kind === 'excel') await api.grc.agenda.exportExcel(false);
      else await api.grc.agenda.exportPdf(false);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Export impossible');
    } finally { setExporting(null); }
  }

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setEvenements((await api.grc.agenda.list(false)).evenements); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function supprimer(e: AgendaEvenement) {
    const ok = await confirm({ title: 'Supprimer cet événement ?', description: e.titre, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.grc.agenda.remove(e.id); await reload(); }
    catch (err) { setError(err instanceof ApiException ? err.message : 'Suppression impossible'); }
  }

  const today = new Date().toISOString().slice(0, 10);
  const aVenir = evenements.filter((e) => (e.dateFin ?? e.dateDebut) >= today);
  const passes = evenements.filter((e) => (e.dateFin ?? e.dateDebut) < today);

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-ink-muted">Vos événements s'affichent aux administrés dans « Vie de la commune », avec ceux des associations.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => void doExport('excel')} disabled={exporting !== null}>
            <FileSpreadsheet className="h-4 w-4" /> <span className="hidden sm:inline">{exporting === 'excel' ? 'Export…' : 'Excel'}</span>
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void doExport('pdf')} disabled={exporting !== null}>
            <FileDown className="h-4 w-4" /> <span className="hidden sm:inline">{exporting === 'pdf' ? 'Export…' : 'PDF'}</span>
          </Button>
          <Button variant="primary" size="sm" onClick={() => setEdit('new')}><Plus className="h-4 w-4" /> Événement</Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : evenements.length === 0 ? (
        <p className="text-sm text-ink-muted">Aucun événement. Créez-en un (conseil municipal, cérémonie, marché…).</p>
      ) : (
        <div className="space-y-4">
          {aVenir.length > 0 && (
            <div>
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-dim">À venir</div>
              <ul className="space-y-2">{aVenir.map((e) => <EvtLigne key={e.id} e={e} onEdit={() => setEdit(e)} onDelete={() => void supprimer(e)} onDiffuser={() => setDiffuse(e)} />)}</ul>
            </div>
          )}
          {passes.length > 0 && (
            <div>
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-dim">Passés</div>
              <ul className="space-y-2 opacity-70">{passes.map((e) => <EvtLigne key={e.id} e={e} onEdit={() => setEdit(e)} onDelete={() => void supprimer(e)} onDiffuser={() => setDiffuse(e)} />)}</ul>
            </div>
          )}
        </div>
      )}

      {edit && (
        <EvenementDialog initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)} onSaved={async () => { setEdit(null); await reload(); }} />
      )}
      {diffuse && (
        <DiffusionDialog e={diffuse} onClose={() => setDiffuse(null)}
          onDone={async () => { setDiffuse(null); await reload(); }} />
      )}
      {dialogElement}
    </div>
  );
}

function EvtLigne({ e, onEdit, onDelete, onDiffuser }: { e: AgendaEvenement; onEdit: () => void; onDelete: () => void; onDiffuser: () => void }) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-r-lg border border-border bg-surface/50 p-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
          <span>{e.titre}</span>
          <Badge variant={CATEGORIE_VARIANT[e.categorie]} size="sm">{CATEGORIE_LABEL[e.categorie]}</Badge>
          {!e.publie && <Badge variant="neutral" size="sm"><EyeOff className="mr-1 inline h-3 w-3" />Brouillon</Badge>}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{frDate(e.dateDebut)}{e.dateFin && e.dateFin !== e.dateDebut ? ` → ${frDate(e.dateFin)}` : ''}</span>
          {e.heureDebut && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{e.heureDebut}{e.heureFin ? ` - ${e.heureFin}` : ''}</span>}
          {e.lieu && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{e.lieu}</span>}
        </div>
        {e.description && <p className="mt-1 text-sm text-ink-muted line-clamp-2">{e.description}</p>}
        {/* Mig 293 — trace de la dernière diffusion : dit qui a été convié,
            et évite le double-envoi par inattention. */}
        {e.derniereDiffusionResume && (
          <p className="mt-1 text-xs text-ink-dim">
            Diffusé{e.derniereDiffusionAt ? ` le ${frDate(e.derniereDiffusionAt.slice(0, 10))}` : ''} : {e.derniereDiffusionResume}
          </p>
        )}
      </div>
      <div className="flex flex-shrink-0 gap-1">
        {/* Tranche E — engendrer un arrêté DEPUIS l'événement. Le texte reprend
            le titre, le lieu et les dates ; l'acte reste rattaché à la fiche.
            C'est le cas de la fête de village : débit de boissons temporaire,
            occupation du domaine public, fermeture de rue. */}
        <Button
          variant="ghost" size="sm" title="Générer un arrêté depuis cet événement"
          onClick={() => { window.location.href = `/actes/nouveau?sourceType=evenement&sourceId=${e.id}`; }}
        >
          <FileSignature className="h-4 w-4" />
        </Button>
        {/* Cadrage 20/08 — convier/diffuser : cloche et/ou e-mail vers
            administrés, salariés, annuaire. Geste séparé de « Publié ». */}
        <Button variant="ghost" size="sm" onClick={onDiffuser} title="Convier / diffuser" aria-label="Convier ou diffuser"><Send className="h-4 w-4" aria-hidden="true" /></Button>
        <Button variant="ghost" size="sm" onClick={onEdit} title="Modifier" aria-label="Modifier l’événement"><Pencil className="h-4 w-4" aria-hidden="true" /></Button>
        <Button variant="ghost" size="sm" onClick={onDelete} title="Supprimer" aria-label="Supprimer l’événement"><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>
      </div>
    </li>
  );
}

function EvenementDialog({ initial, onClose, onSaved }: { initial: AgendaEvenement | null; onClose: () => void; onSaved: () => void }) {
  const [titre, setTitre] = useState(initial?.titre ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [categorie, setCategorie] = useState<AgendaCategorie>(initial?.categorie ?? 'autre');
  const [lieu, setLieu] = useState(initial?.lieu ?? '');
  const [dateDebut, setDateDebut] = useState(initial?.dateDebut ?? new Date().toISOString().slice(0, 10));
  const [heureDebut, setHeureDebut] = useState(initial?.heureDebut ?? '');
  const [dateFin, setDateFin] = useState(initial?.dateFin ?? '');
  const [heureFin, setHeureFin] = useState(initial?.heureFin ?? '');
  const [publie, setPublie] = useState(initial?.publie ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {
        titre: titre.trim(), description: description.trim() || null, categorie,
        lieu: lieu.trim() || null, dateDebut,
        heureDebut: heureDebut || null, dateFin: dateFin || null, heureFin: heureFin || null, publie,
      };
      if (initial) await api.grc.agenda.update(initial.id, body);
      else await api.grc.agenda.create(body);
      onSaved();
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible'); setBusy(false); }
  }

  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier l\'événement' : 'Nouvel événement'}>
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <Field label="Titre" htmlFor="ag-titre" required>
          <Input id="ag-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Conseil municipal, Marché de Noël…" />
        </Field>
        <Field label="Catégorie" htmlFor="ag-cat">
          <select id="ag-cat" className={selectCls} value={categorie} onChange={(e) => setCategorie(e.target.value as AgendaCategorie)}>
            {Object.entries(CATEGORIE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Lieu (optionnel)" htmlFor="ag-lieu">
          <Input id="ag-lieu" value={lieu} onChange={(e) => setLieu(e.target.value)} placeholder="Salle des fêtes, place de la mairie…" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date de début" htmlFor="ag-dd" required><Input id="ag-dd" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} /></Field>
          <Field label="Heure de début" htmlFor="ag-hd"><Input id="ag-hd" type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date de fin (optionnel)" htmlFor="ag-df"><Input id="ag-df" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} /></Field>
          <Field label="Heure de fin" htmlFor="ag-hf"><Input id="ag-hf" type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} /></Field>
        </div>
        <Field label="Description (optionnel)" htmlFor="ag-desc">
          <textarea id="ag-desc" rows={3}
            className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={publie} onChange={(e) => setPublie(e.target.checked)} className="h-4 w-4 rounded border-border text-grc focus-visible:ring-grc" />
          Publié (visible des administrés). Décochez pour préparer sans afficher.
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || !titre.trim()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * DiffusionDialog — convier/diffuser un événement (cadrage 20/08, mig 293).
 *
 * Les trois décisions d'Henrico, traduites à l'écran :
 *   1. cibles à COCHER : tous les administrés, tous les salariés mairie, et/ou
 *      des catégories de l'annuaire existant — pas de nouvelle liste ;
 *   2. canaux : cloche et/ou e-mail — pas de SMS ;
 *   3. la diffusion est un GESTE, jamais un effet de « Publié ». Un événement
 *      non publié se diffuse (convier sans afficher), mais pas vers les
 *      administrés : le serveur le refuse avec un message clair.
 * Les contacts d'annuaire n'ont pas de compte : eux ne reçoivent que l'e-mail.
 */
function DiffusionDialog({ e, onClose, onDone }: { e: AgendaEvenement; onClose: () => void; onDone: () => void }) {
  const [administres, setAdministres] = useState(false);
  const [salaries, setSalaries] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [cloche, setCloche] = useState(true);
  const [email, setEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fait, setFait] = useState<string | null>(null);

  const cibleOk = administres || salaries || categories.length > 0;
  const canalOk = cloche || email;

  function toggleCat(v: string) {
    setCategories((c) => (c.includes(v) ? c.filter((x) => x !== v) : [...c, v]));
  }

  async function envoyer() {
    setBusy(true); setErr(null);
    try {
      const r = await api.grc.agenda.diffuser(e.id, {
        cibles: { administres, salaries, annuaireCategories: categories.length ? categories : undefined },
        canaux: { cloche, email },
      });
      setFait(r.resume);
    } catch (ex) {
      setErr(ex instanceof ApiException ? ex.payload.error : 'Diffusion impossible');
    } finally { setBusy(false); }
  }

  const caseCls = 'flex items-center gap-2 text-sm text-ink';

  return (
    <Dialog open onClose={onClose} title={`Convier / diffuser — ${e.titre}`}>
      <div className="space-y-4">
        {err && <Alert variant="error">{err}</Alert>}
        {fait ? (
          <>
            <Alert variant="success">Diffusion envoyée : {fait}.</Alert>
            <div className="flex justify-end">
              <Button onClick={onDone}>Fermer</Button>
            </div>
          </>
        ) : (
          <>
            {!e.publie && (
              <Alert variant="warning">
                Événement non publié : vous pouvez convier les salariés ou l'annuaire,
                mais pas les administrés (publiez-le d'abord).
              </Alert>
            )}
            <div>
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-dim">Qui convier ?</div>
              <div className="space-y-1.5">
                <label className={caseCls}>
                  <input type="checkbox" checked={administres} disabled={!e.publie}
                    onChange={(ev) => setAdministres(ev.target.checked)} />
                  Tous les administrés
                </label>
                <label className={caseCls}>
                  <input type="checkbox" checked={salaries} onChange={(ev) => setSalaries(ev.target.checked)} />
                  Tous les salariés de la mairie
                </label>
                <div className="pt-1 text-xs text-ink-dim">Depuis l'annuaire (e-mail uniquement — pas de compte, pas de cloche) :</div>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {TIERS_CATEGORIES_USAGE.map((c) => (
                    <label key={c.value} className={caseCls}>
                      <input type="checkbox" checked={categories.includes(c.value)} onChange={() => toggleCat(c.value)} />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-dim">Par quel canal ?</div>
              <div className="flex gap-4">
                <label className={caseCls}>
                  <input type="checkbox" checked={cloche} onChange={(ev) => setCloche(ev.target.checked)} />
                  Cloche (dans l'application)
                </label>
                <label className={caseCls}>
                  <input type="checkbox" checked={email} onChange={(ev) => setEmail(ev.target.checked)} />
                  E-mail
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Annuler</Button>
              <Button onClick={() => void envoyer()} disabled={busy || !cibleOk || !canalOk}>
                <Send className="h-4 w-4" /> {busy ? 'Envoi…' : 'Diffuser'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
